/*
 * MagBak cart relationship contract.
 *
 * Phase 2 status: development pilot. The library is dependency-free and inert
 * unless cart lines contain the versioned relationship property.
 */
(function attachMagBakCartRelationship(factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof document !== 'undefined' && document.defaultView) {
    document.defaultView.MagBakCartRelationship = api;
  }
})(function createContract() {
  'use strict';

  const VERSION = '1';
  const PROPERTY = '_mb_link';
  const MAX_LINK_LENGTH = 64;
  const KINDS = Object.freeze({
    ROOT: 'r',
    DEPENDENT: 'd',
    PERSONALIZATION: 'p',
    INCLUDED: 'i',
    BUNDLE_PEER: 'b',
  });
  const KIND_VALUES = new Set(Object.values(KINDS));
  const GROUP_PATTERN = /^[A-Za-z0-9_-]{12,48}$/;
  const NODE_PATTERN = /^[0-9a-z]{1,4}$/;

  class RelationshipError extends Error {
    constructor(code, message) {
      super(message);
      this.name = 'RelationshipError';
      this.code = code;
    }
  }

  const asText = (value) => value === null || value === undefined ? '' : String(value);

  function assert(condition, code, message) {
    if (!condition) throw new RelationshipError(code, message);
  }

  function validateLinkParts(parts) {
    assert(parts && typeof parts === 'object', 'invalid_link', 'Relationship data must be an object.');
    const version = asText(parts.version || VERSION);
    const group = asText(parts.group);
    const node = asText(parts.node);
    const parent = parts.parent === null || parts.parent === undefined || parts.parent === '-'
      ? null
      : asText(parts.parent);
    const kind = asText(parts.kind);

    assert(version === VERSION, 'unsupported_version', `Unsupported relationship version: ${version || '(blank)'}.`);
    assert(GROUP_PATTERN.test(group), 'invalid_group', 'Group tokens must be 12-48 URL-safe characters.');
    assert(NODE_PATTERN.test(node), 'invalid_node', 'Node identifiers must be 1-4 lowercase base36 characters.');
    assert(parent === null || NODE_PATTERN.test(parent), 'invalid_parent', 'Parent identifiers must be null or lowercase base36.');
    assert(parent !== node, 'self_parent', 'A relationship node cannot parent itself.');
    assert(KIND_VALUES.has(kind), 'invalid_kind', `Unknown relationship kind: ${kind || '(blank)'}.`);
    assert(kind !== KINDS.ROOT || parent === null, 'root_with_parent', 'Root relationships cannot have a parent.');
    assert(kind !== KINDS.BUNDLE_PEER || parent === null, 'bundle_peer_with_parent', 'Bundle peers cannot have a parent.');
    assert(kind === KINDS.ROOT || kind === KINDS.BUNDLE_PEER || parent !== null, 'missing_parent', 'Child relationships require a parent.');

    return { version, group, node, parent, kind };
  }

  function encodeLink(parts) {
    const valid = validateLinkParts(parts);
    const value = [valid.version, valid.group, valid.node, valid.parent || '-', valid.kind].join('.');
    assert(value.length <= MAX_LINK_LENGTH, 'link_too_long', `Relationship value exceeds ${MAX_LINK_LENGTH} characters.`);
    return value;
  }

  function parseLink(value) {
    const text = asText(value);
    assert(text.length > 0, 'empty_link', 'Relationship value is blank.');
    assert(text.length <= MAX_LINK_LENGTH, 'link_too_long', `Relationship value exceeds ${MAX_LINK_LENGTH} characters.`);
    const fields = text.split('.');
    assert(fields.length === 5, 'invalid_shape', 'Relationship values require version, group, node, parent, and kind.');
    const valid = validateLinkParts({
      version: fields[0],
      group: fields[1],
      node: fields[2],
      parent: fields[3],
      kind: fields[4],
    });
    return { ...valid, value: text };
  }

  function safeParseLink(value) {
    try {
      return { ok: true, link: parseLink(value), error: null };
    } catch (error) {
      return {
        ok: false,
        link: null,
        error: {
          code: error instanceof RelationshipError ? error.code : 'unknown_error',
          message: error.message,
        },
      };
    }
  }

  function base64Url(bytes) {
    if (typeof Buffer !== 'undefined') return Buffer.from(bytes).toString('base64url');
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
  }

  function generateGroupToken(randomBytes) {
    let bytes = randomBytes;
    if (!bytes) {
      assert(rootCrypto(), 'missing_crypto', 'Secure random generation is unavailable.');
      bytes = new Uint8Array(9);
      rootCrypto().getRandomValues(bytes);
    }
    assert(bytes.length === 9, 'invalid_entropy', 'Group tokens require exactly 9 random bytes.');
    const token = base64Url(bytes);
    assert(GROUP_PATTERN.test(token), 'invalid_generated_group', 'Generated group token did not match the contract.');
    return token;
  }

  function encodeBundlePricingGroup(tiers, randomBytes) {
    assert(Array.isArray(tiers) && tiers.length > 0, 'invalid_pricing_tiers', 'Bundle pricing requires at least one tier.');
    assert(tiers.length <= 4, 'too_many_pricing_tiers', 'Compact bundle pricing supports at most four tiers.');

    const normalized = tiers.map((tier) => {
      const count = Number(tier?.count);
      const rawPercent = Number(tier?.percent);
      const percent = Math.round(rawPercent * 1000000) / 1000000;
      assert(Number.isInteger(count) && count >= 1 && count <= 99, 'invalid_pricing_count', 'Bundle tier counts must be whole numbers from 1 to 99.');
      assert(Number.isFinite(percent) && percent > 0 && percent <= 100, 'invalid_pricing_percent', 'Bundle tier percentages must be greater than 0 and no more than 100.');
      return `${count}x${String(percent).replace('.', 'p')}`;
    });
    const token = `t${normalized.join('-')}_${generateGroupToken(randomBytes)}`;
    assert(GROUP_PATTERN.test(token), 'pricing_group_too_long', 'The compact bundle pricing profile is too long for one relationship group.');
    return token;
  }

  function rootCrypto() {
    if (typeof document !== 'undefined' && document.defaultView?.crypto) return document.defaultView.crypto;
    return typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  }

  function newRelation(line, index, issues) {
    const properties = line.properties || {};
    if (!properties[PROPERTY]) return null;
    const parsed = safeParseLink(properties[PROPERTY]);
    if (!parsed.ok) {
      issues.push({
        code: parsed.error.code,
        lineKey: line.key,
        message: parsed.error.message,
      });
      return null;
    }
    return { ...parsed.link, lineKey: line.key, index, source: 'v1' };
  }

  function legacyCartRelations(lines, relationsByKey) {
    const groups = new Map();
    lines.forEach((line, index) => {
      // A line claiming the new contract must never fall back to an older
      // contract if its value is malformed. Surface the error and fail closed.
      if (relationsByKey.has(line.key) || line.properties?.[PROPERTY]) return;
      const groupValue = asText(line.properties?._mb_cart_group);
      if (!groupValue) return;
      const group = `legacy-cart:${groupValue}`;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push({ line, index });
    });

    groups.forEach((entries, group) => {
      const rootEntry = entries.find(({ line }) => (
        line.properties?._mb_cart_role === 'main'
        || asText(line.properties?._mb_cart_order) === '0'
      )) || entries[0];
      const rootNode = legacyNode(rootEntry.line, rootEntry.index);
      const pairedGroup = entries.some(({ line }) => line.properties?._mb_cart_role === 'paired-component');
      entries.forEach(({ line, index }) => {
        const role = asText(line.properties?._mb_cart_role);
        const node = legacyNode(line, index);
        const isRoot = line.key === rootEntry.line.key;
        const kind = pairedGroup && (isRoot || role === 'paired-component')
          ? KINDS.BUNDLE_PEER
          : isRoot
            ? KINDS.ROOT
            : KINDS.DEPENDENT;
        relationsByKey.set(line.key, {
          version: 'legacy',
          group,
          node,
          parent: kind === KINDS.DEPENDENT ? rootNode : null,
          kind,
          lineKey: line.key,
          index,
          source: 'legacy-cart',
        });
      });
    });
  }

  function legacyNode(line, index) {
    const order = Number.parseInt(line.properties?._mb_cart_order, 10);
    return Number.isInteger(order) && order >= 0 ? order.toString(36) : index.toString(36);
  }

  function legacyBuilderRelations(lines, relationsByKey) {
    const groupEntries = new Map();
    lines.forEach((line, index) => {
      if (relationsByKey.has(line.key) || line.properties?.[PROPERTY]) return;
      const properties = line.properties || {};
      const groupValue = asText(properties._bundle_key || properties._mb_bb_parent);
      if (!groupValue) return;
      const group = `legacy-builder:${groupValue}`;
      if (!groupEntries.has(group)) groupEntries.set(group, []);
      groupEntries.get(group).push({ line, index });
    });

    groupEntries.forEach((entries, group) => {
      const primaries = entries.filter(({ line }) => Boolean(line.properties?._bundle_key));
      const primaryNodes = new Map(primaries.map(({ line, index }) => [
        asText(line.variant_id || line.id),
        index.toString(36),
      ]));
      const fallbackParent = primaries.length ? primaries[0].index.toString(36) : null;

      entries.forEach(({ line, index }) => {
        const properties = line.properties || {};
        const primary = Boolean(properties._bundle_key);
        const role = asText(properties._mb_bb_role);
        const parent = primary
          ? null
          : primaryNodes.get(asText(properties._mb_bb_parent_variant)) || fallbackParent;
        relationsByKey.set(line.key, {
          version: 'legacy',
          group,
          node: index.toString(36),
          parent,
          kind: primary ? KINDS.BUNDLE_PEER : role === 'customization' ? KINDS.PERSONALIZATION : KINDS.DEPENDENT,
          lineKey: line.key,
          index,
          source: 'legacy-builder',
        });
      });
    });
  }

  function normalizeLines(inputLines) {
    return (inputLines || []).map((line, index) => ({
      ...line,
      key: asText(line.key || `line-${index}`),
      quantity: Math.max(0, Number(line.quantity || 0)),
      properties: line.properties || {},
    }));
  }

  function buildGraph(inputLines, options = {}) {
    const lines = normalizeLines(inputLines);
    const issues = [];
    const relationsByKey = new Map();
    lines.forEach((line, index) => {
      const relation = newRelation(line, index, issues);
      if (relation) relationsByKey.set(line.key, relation);
    });
    if (options.includeLegacy !== false) {
      legacyCartRelations(lines, relationsByKey);
      legacyBuilderRelations(lines, relationsByKey);
    }

    const groups = new Map();
    relationsByKey.forEach((relation) => {
      if (!groups.has(relation.group)) groups.set(relation.group, []);
      groups.get(relation.group).push(relation);
    });

    groups.forEach((relations, group) => {
      const nodes = new Map();
      relations.forEach((relation) => {
        if (nodes.has(relation.node)) {
          issues.push({ code: 'duplicate_node', group, lineKey: relation.lineKey, node: relation.node });
          return;
        }
        nodes.set(relation.node, relation);
      });
      relations.forEach((relation) => {
        if (relation.parent !== null && !nodes.has(relation.parent)) {
          issues.push({ code: 'orphan_parent', group, lineKey: relation.lineKey, parent: relation.parent });
        }
      });
      detectCycles(relations, nodes, issues);
    });

    return { lines, relationsByKey, groups, issues };
  }

  function detectCycles(relations, nodes, issues) {
    relations.forEach((relation) => {
      const visited = new Set([relation.node]);
      let current = relation;
      while (current.parent !== null && nodes.has(current.parent)) {
        if (visited.has(current.parent)) {
          issues.push({ code: 'cycle', group: relation.group, lineKey: relation.lineKey });
          return;
        }
        visited.add(current.parent);
        current = nodes.get(current.parent);
      }
    });
  }

  function descendants(graph, relation) {
    const group = graph.groups.get(relation.group) || [];
    const nodeIds = new Set([relation.node]);
    let changed = true;
    while (changed) {
      changed = false;
      group.forEach((candidate) => {
        if (candidate.parent !== null && nodeIds.has(candidate.parent) && !nodeIds.has(candidate.node)) {
          nodeIds.add(candidate.node);
          changed = true;
        }
      });
    }
    return group.filter((candidate) => nodeIds.has(candidate.node));
  }

  function removalPlan(inputLines, targetLineKey, options = {}) {
    const graph = buildGraph(inputLines, options);
    const relation = graph.relationsByKey.get(asText(targetLineKey));
    if (!relation) return { lineKeys: [asText(targetLineKey)], graph };
    const group = graph.groups.get(relation.group) || [];
    const selected = relation.kind === KINDS.BUNDLE_PEER
      ? group
      : descendants(graph, relation);
    const keys = new Set(selected.map((item) => item.lineKey));
    return {
      lineKeys: graph.lines.filter((line) => keys.has(line.key)).map((line) => line.key),
      graph,
    };
  }

  function validateQuantities(inputLines, options = {}) {
    const graph = buildGraph(inputLines, options);
    const violations = [];
    const linesByKey = new Map(graph.lines.map((line) => [line.key, line]));

    graph.groups.forEach((relations, group) => {
      const nodes = new Map(relations.map((relation) => [relation.node, relation]));
      const peerQuantities = relations
        .filter((relation) => relation.kind === KINDS.BUNDLE_PEER)
        .map((relation) => linesByKey.get(relation.lineKey)?.quantity || 0);
      if (peerQuantities.length > 1 && new Set(peerQuantities).size > 1) {
        violations.push({ code: 'bundle_quantity_mismatch', group });
      }

      relations.forEach((relation) => {
        if (relation.parent === null || !nodes.has(relation.parent)) return;
        const childQuantity = linesByKey.get(relation.lineKey)?.quantity || 0;
        const parentRelation = nodes.get(relation.parent);
        const parentQuantity = linesByKey.get(parentRelation.lineKey)?.quantity || 0;
        if (relation.kind === KINDS.INCLUDED && childQuantity !== parentQuantity) {
          violations.push({ code: 'included_quantity_mismatch', lineKey: relation.lineKey, parentLineKey: parentRelation.lineKey });
        } else if (relation.kind !== KINDS.INCLUDED && childQuantity > parentQuantity) {
          violations.push({ code: 'dependent_quantity_exceeds_parent', lineKey: relation.lineKey, parentLineKey: parentRelation.lineKey });
        }
      });
    });

    return { graph, violations };
  }

  function quantityUpdatePlan(inputLines, targetLineKey, requestedQuantity, options = {}) {
    const graph = buildGraph(inputLines, options);
    assert(graph.issues.length === 0, 'invalid_graph', 'Cannot update quantities on an invalid relationship graph.');
    const targetKey = asText(targetLineKey);
    const targetLine = graph.lines.find((line) => line.key === targetKey);
    assert(targetLine, 'missing_target_line', 'The target cart line was not found.');
    const quantity = Number(requestedQuantity);
    assert(Number.isInteger(quantity) && quantity >= 0, 'invalid_requested_quantity', 'Requested quantity must be a non-negative integer.');

    const targetRelation = graph.relationsByKey.get(targetKey);
    if (!targetRelation) return { updates: { [targetKey]: quantity }, graph };

    const group = graph.groups.get(targetRelation.group) || [];
    const relationsByNode = new Map(group.map((relation) => [relation.node, relation]));
    const linesByKey = new Map(graph.lines.map((line) => [line.key, line]));
    const updates = { [targetKey]: quantity };

    if (targetRelation.kind === KINDS.BUNDLE_PEER) {
      group
        .filter((relation) => relation.kind === KINDS.BUNDLE_PEER)
        .forEach((relation) => { updates[relation.lineKey] = quantity; });
    }

    const descendants = group.filter((relation) => relation.parent !== null);
    for (let pass = 0; pass < group.length; pass += 1) {
      let changed = false;
      descendants.forEach((relation) => {
        const parentRelation = relationsByNode.get(relation.parent);
        if (!parentRelation) return;
        const parentPlanned = Object.hasOwn(updates, parentRelation.lineKey)
          ? updates[parentRelation.lineKey]
          : linesByKey.get(parentRelation.lineKey)?.quantity;
        if (parentPlanned === undefined) return;
        const current = linesByKey.get(relation.lineKey)?.quantity || 0;
        const childPlanned = Object.hasOwn(updates, relation.lineKey)
          ? updates[relation.lineKey]
          : current;
        const planned = relation.kind === KINDS.INCLUDED
          ? parentPlanned
          : Math.min(childPlanned, parentPlanned);
        if (planned !== childPlanned) {
          updates[relation.lineKey] = planned;
          changed = true;
        }
      });
      if (!changed) break;
    }

    return { updates, graph };
  }

  function developmentPilotEnabled(options = {}) {
    const hostname = asText(options.hostname).toLowerCase();
    const themeRole = asText(options.themeRole).toLowerCase();
    const previewContext = hostname === 'localhost'
      || hostname === '127.0.0.1'
      || options.themePreview === true
      || options.designMode === true
      || themeRole === 'unpublished'
      || themeRole === 'development';
    const allowedHandles = Array.isArray(options.allowedProductHandles)
      ? options.allowedProductHandles.map(asText)
      : [];
    const productEligible = options.productEligible === true
      || (asText(options.allowedProductHandle) !== ''
        && asText(options.productHandle) === asText(options.allowedProductHandle))
      || allowedHandles.includes(asText(options.productHandle));
    return options.requested === true
      && previewContext
      && productEligible;
  }

  function planNewRelationshipGroup(dependentCount, options = {}) {
    const count = Number(dependentCount);
    assert(Number.isSafeInteger(count) && count > 0, 'invalid_dependent_count', 'A new relationship group requires at least one dependent.');
    assert(count < (36 ** 4), 'node_space_exhausted', 'Too many dependents were requested for one relationship group.');
    const childKinds = Array.isArray(options.childKinds)
      ? options.childKinds.map(asText)
      : Array.from({ length: count }, () => KINDS.DEPENDENT);
    assert(childKinds.length === count, 'invalid_child_kind_count', 'A relationship group requires one child kind per dependent.');
    childKinds.forEach((kind) => {
      assert(
        kind === KINDS.DEPENDENT || kind === KINDS.PERSONALIZATION,
        'invalid_child_kind',
        `Unsupported product relationship child kind: ${kind || '(blank)'}.`
      );
    });
    const group = generateGroupToken(options.randomBytes);
    const rootNode = '0';
    return {
      group,
      rootLink: encodeLink({ group, node: rootNode, parent: null, kind: KINDS.ROOT }),
      dependentLinks: Array.from({ length: count }, (_, index) => encodeLink({
        group,
        node: (index + 1).toString(36),
        parent: rootNode,
        kind: childKinds[index],
      })),
    };
  }

  function planNewBundleRelationshipGroup(primaryPlans, options = {}) {
    assert(Array.isArray(primaryPlans) && primaryPlans.length > 0, 'invalid_primary_plans', 'A bundle relationship group requires at least one primary.');

    const supportedChildKinds = new Set([
      KINDS.DEPENDENT,
      KINDS.PERSONALIZATION,
      KINDS.INCLUDED,
    ]);
    const normalizedPlans = primaryPlans.map((plan) => {
      const childKinds = Array.isArray(plan?.childKinds) ? plan.childKinds.map(asText) : [];
      childKinds.forEach((kind) => {
        assert(supportedChildKinds.has(kind), 'invalid_bundle_child_kind', `Unsupported bundle child kind: ${kind || '(blank)'}.`);
      });
      return { childKinds };
    });
    const nodeCount = normalizedPlans.reduce((total, plan) => total + 1 + plan.childKinds.length, 0);
    assert(nodeCount < (36 ** 4), 'node_space_exhausted', 'Too many bundle lines were requested for one relationship group.');

    const group = options.tiers
      ? encodeBundlePricingGroup(options.tiers, options.randomBytes)
      : generateGroupToken(options.randomBytes);
    const primaryNodes = normalizedPlans.map((_, index) => index.toString(36));
    let nextNode = primaryNodes.length;
    const primaryLinks = primaryNodes.map((node) => encodeLink({
      group,
      node,
      parent: null,
      kind: KINDS.BUNDLE_PEER,
    }));
    const childLinks = normalizedPlans.map((plan, primaryIndex) => plan.childKinds.map((kind) => {
      const link = encodeLink({
        group,
        node: (nextNode++).toString(36),
        parent: primaryNodes[primaryIndex],
        kind,
      });
      return link;
    }));

    return {
      group,
      primaryLinks,
      childLinks,
    };
  }

  function nextNodeId(inputLines, group) {
    const graph = buildGraph(inputLines);
    const used = new Set((graph.groups.get(asText(group)) || []).map((relation) => relation.node));
    for (let value = 0; value < (36 ** 4); value += 1) {
      const candidate = value.toString(36);
      if (!used.has(candidate)) return candidate;
    }
    throw new RelationshipError('node_space_exhausted', 'No relationship node identifiers remain in this group.');
  }

  function attachmentEligibility(inputLines, parentLineKey, options = {}) {
    const graph = buildGraph(inputLines, options);
    const parentKey = asText(parentLineKey);
    const parentLine = graph.lines.find((line) => line.key === parentKey) || null;
    if (!parentLine) return { eligible: false, reason: 'missing_parent_line', graph };
    if (graph.issues.length) return { eligible: false, reason: 'invalid_graph', graph };

    const parentRelation = graph.relationsByKey.get(parentKey) || null;
    if (parentRelation && parentRelation.source !== 'v1') {
      return { eligible: false, reason: 'legacy_relationship', graph, parentRelation };
    }

    return { eligible: true, reason: null, graph, parentRelation };
  }

  function planPersonalizationAttachment(inputLines, parentLineKey, options = {}) {
    const eligibility = attachmentEligibility(inputLines, parentLineKey, options);
    const graph = eligibility.graph;
    assert(eligibility.reason !== 'invalid_graph', 'invalid_graph', 'Cannot attach personalization to an invalid relationship graph.');
    const parentLine = graph.lines.find((line) => line.key === asText(parentLineKey));
    assert(parentLine, 'missing_parent_line', 'The parent cart line was not found.');
    assert(parentLine.quantity > 0, 'invalid_parent_quantity', 'The parent quantity must be positive.');
    const parentRelation = graph.relationsByKey.get(parentLine.key) || null;
    assert(
      eligibility.eligible,
      'legacy_attachment_not_supported',
      'Personalization is available after this item is added using the current cart format.'
    );
    const requiresQuantitySplit = parentLine.quantity > 1;
    const modernParentRelation = parentRelation?.source === 'v1' ? parentRelation : null;
    let group = modernParentRelation?.group || generateGroupToken(options.randomBytes);
    let parentNode = modernParentRelation?.node || '0';
    let parentKind = modernParentRelation?.kind || KINDS.ROOT;
    const originalParentLink = parentRelation?.source === 'v1'
      ? parentRelation.value
      : encodeLink({ group, node: parentNode, parent: null, kind: KINDS.ROOT });
    let parentLink = originalParentLink;
    let splitParentLink = null;

    if (requiresQuantitySplit) {
      if (!parentRelation || parentKind === KINDS.ROOT) {
        group = generateGroupToken(options.splitRandomBytes || options.randomBytes);
        parentNode = '0';
        parentKind = KINDS.ROOT;
        splitParentLink = encodeLink({ group, node: parentNode, parent: null, kind: parentKind });
      } else {
        parentNode = nextNodeId(graph.lines, group);
        splitParentLink = encodeLink({
          group,
          node: parentNode,
          parent: parentRelation.parent,
          kind: parentKind,
        });
      }
      parentLink = splitParentLink;
    }
    const childNode = nextNodeId(
      [...graph.lines, { key: '__planned-parent__', quantity: 1, properties: { [PROPERTY]: parentLink } }],
      group
    );
    const childLink = encodeLink({ group, node: childNode, parent: parentNode, kind: KINDS.PERSONALIZATION });

    return {
      requiresParentRewrite: !requiresQuantitySplit && (!parentRelation || parentRelation.source !== 'v1'),
      requiresQuantitySplit,
      parentLineKey: parentLine.key,
      parentLink,
      originalParentLink,
      splitParentLink,
      personalizedQuantity: 1,
      remainderQuantity: Math.max(0, parentLine.quantity - 1),
      childLink,
      group,
      parentNode,
      childNode,
      recovery: {
        beforeChildAddFailure: 'restore-original-parent-line',
        afterChildAddFailure: 'remove-orphan-child-then-restore-original-parent-line',
        afterAuthoritativeRefresh: 'rebuild-plan-from-current-cart',
      },
    };
  }

  function planDependentAttachment(inputLines, parentLineKey, options = {}) {
    const eligibility = attachmentEligibility(inputLines, parentLineKey, options);
    const graph = eligibility.graph;
    assert(eligibility.reason !== 'invalid_graph', 'invalid_graph', 'Cannot attach an add-on to an invalid relationship graph.');
    const parentLine = graph.lines.find((line) => line.key === asText(parentLineKey));
    assert(parentLine, 'missing_parent_line', 'The parent cart line was not found.');
    assert(parentLine.quantity > 0, 'invalid_parent_quantity', 'The parent quantity must be positive.');
    const parentRelation = graph.relationsByKey.get(parentLine.key) || null;
    assert(
      eligibility.eligible,
      'legacy_attachment_not_supported',
      'This add-on is available after the parent item is added using the current cart format.'
    );

    const modernParentRelation = parentRelation?.source === 'v1' ? parentRelation : null;
    const group = modernParentRelation?.group || generateGroupToken(options.randomBytes);
    const parentNode = modernParentRelation?.node || '0';
    const parentLink = modernParentRelation?.value
      || encodeLink({ group, node: parentNode, parent: null, kind: KINDS.ROOT });
    const childNode = nextNodeId(
      [...graph.lines, { key: '__planned-parent__', quantity: parentLine.quantity, properties: { [PROPERTY]: parentLink } }],
      group
    );
    const childLink = encodeLink({ group, node: childNode, parent: parentNode, kind: KINDS.DEPENDENT });

    return {
      requiresParentRewrite: !parentRelation || parentRelation.source !== 'v1',
      requiresQuantitySplit: false,
      parentLineKey: parentLine.key,
      parentLink,
      originalParentLink: modernParentRelation?.value || null,
      splitParentLink: null,
      personalizedQuantity: 0,
      remainderQuantity: parentLine.quantity,
      childLink,
      group,
      parentNode,
      childNode,
      recovery: {
        beforeChildAddFailure: 'restore-original-parent-line',
        afterChildAddFailure: 'remove-orphan-child-then-restore-original-parent-line',
        afterAuthoritativeRefresh: 'rebuild-plan-from-current-cart',
      },
    };
  }

  function fulfillmentProperties({ link, engravingText, parentSku } = {}) {
    const output = {};
    if (engravingText !== undefined && engravingText !== null && asText(engravingText) !== '') {
      output['Engrave Text'] = asText(engravingText);
    }
    if (parentSku !== undefined && parentSku !== null && asText(parentSku) !== '') {
      output._gid = asText(parentSku);
    }
    if (link !== undefined && link !== null && asText(link) !== '') {
      output[PROPERTY] = encodeLink(typeof link === 'string' ? parseLink(link) : link);
    }
    return output;
  }

  function propertyCharacterBudget(properties) {
    return Object.entries(properties || {}).reduce((total, [key, value], index) => (
      total + (index ? 2 : 0) + asText(key).length + 2 + asText(value).length
    ), 0);
  }

  return Object.freeze({
    VERSION,
    PROPERTY,
    MAX_LINK_LENGTH,
    KINDS,
    RelationshipError,
    encodeLink,
    parseLink,
    safeParseLink,
    generateGroupToken,
    encodeBundlePricingGroup,
    buildGraph,
    removalPlan,
    validateQuantities,
    quantityUpdatePlan,
    developmentPilotEnabled,
    attachmentEligibility,
    planNewRelationshipGroup,
    planNewBundleRelationshipGroup,
    planPersonalizationAttachment,
    planDependentAttachment,
    fulfillmentProperties,
    propertyCharacterBudget,
  });
});
